'use client';

import { useState, useEffect } from 'react';
import ContentCard from './ContentCard';
import FilterBar from './FilterBar';
import ContentViewer from './ContentViewer';
import TierCard from './TierCard';
import StatsOverview from './StatsOverview';
import Icon from '@/components/ui/AppIcon';

interface ContentItem {
  id: string;
  title: string;
  description: string;
  type: 'video' | 'audio' | 'photo' | 'document';
  thumbnail: string;
  alt: string;
  fullContent: string;
  unlockedDate: string;
  duration?: string;
  size?: string;
  isPremium: boolean;
  contributorsCount: number;
  category: string;
  unlockRequirement: string;
}

interface Tier {
  id: string;
  name: string;
  description: string;
  requirement: string;
  contentCount: number;
  isUnlocked: boolean;
  progress: number;
  icon: string;
  color: string;
}

interface FilterOption {
  id: string;
  label: string;
  count: number;
}

interface Stat {
  label: string;
  value: string;
  icon: string;
  change?: string;
  changeType?: 'positive' | 'negative' | 'neutral';
}

const UnlockedContentGalleryInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedContent, setSelectedContent] = useState<ContentItem | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeView, setActiveView] = useState<'gallery' | 'tiers'>('gallery');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const mockStats: Stat[] = [
  {
    label: "Total Unlocked Content",
    value: "847",
    icon: "PhotoIcon",
    change: "+23",
    changeType: "positive"
  },
  {
    label: "Premium Content",
    value: "156",
    icon: "StarIcon",
    change: "+8",
    changeType: "positive"
  },
  {
    label: "Community Contributors",
    value: "50,247",
    icon: "UserGroupIcon",
    change: "+1,247",
    changeType: "positive"
  },
  {
    label: "Hours of Content",
    value: "2,847",
    icon: "ClockIcon",
    change: "+127",
    changeType: "positive"
  }];


  const mockTiers: Tier[] = [
  {
    id: "bronze",
    name: "Bronze Vault",
    description: "Basic exclusive content including behind-the-scenes photos, early demos, and community highlights.",
    requirement: "Complete 5 community quests",
    contentCount: 234,
    isUnlocked: true,
    progress: 100,
    icon: "ShieldCheckIcon",
    color: "bronze"
  },
  {
    id: "silver",
    name: "Silver Archive",
    description: "Enhanced content with rare recordings, extended interviews, and exclusive rehearsal footage.",
    requirement: "Complete 15 community quests + 500 contribution points",
    contentCount: 156,
    isUnlocked: true,
    progress: 100,
    icon: "TrophyIcon",
    color: "silver"
  },
  {
    id: "gold",
    name: "Gold Collection",
    description: "Premium content featuring unreleased tracks, studio sessions, and personal band commentary.",
    requirement: "Complete 30 community quests + 2,000 contribution points",
    contentCount: 89,
    isUnlocked: true,
    progress: 100,
    icon: "StarIcon",
    color: "gold"
  },
  {
    id: "platinum",
    name: "Platinum Sanctuary",
    description: "Ultra-exclusive content with never-before-seen footage, master recordings, and direct band interactions.",
    requirement: "Complete 50 community quests + 5,000 contribution points + Golden Ticket",
    contentCount: 42,
    isUnlocked: false,
    progress: 73,
    icon: "SparklesIcon",
    color: "platinum"
  }];


  const mockContent: ContentItem[] = [
  {
    id: "1",
    title: "Master of Puppets - Studio Session Raw Footage",
    description: "Exclusive behind-the-scenes footage from the Master of Puppets recording sessions, featuring candid moments and creative process insights.",
    type: "video",
    thumbnail: "https://images.unsplash.com/photo-1568378910159-c0cd1844e439",
    alt: "Black and white photo of recording studio with vintage mixing console and microphones",
    fullContent: "https://images.unsplash.com/photo-1630511697517-ced117bd0f35",
    unlockedDate: "2024-11-15",
    duration: "47:23",
    isPremium: true,
    contributorsCount: 2847,
    category: "Studio Sessions",
    unlockRequirement: "Community completed 'Master Quest: Studio Secrets' with 2,847 contributions"
  },
  {
    id: "2",
    title: "Rare Acoustic Demo - Nothing Else Matters",
    description: "Previously unreleased acoustic version of Nothing Else Matters, recorded during the Black Album sessions.",
    type: "audio",
    thumbnail: "https://images.unsplash.com/photo-1708246308126-cc5c6184787a",
    alt: "Close-up of acoustic guitar strings and sound hole with warm lighting",
    fullContent: "https://img.rocket.new/generatedImages/rocket_gen_img_1189f1d23-1763436190995.png",
    unlockedDate: "2024-11-12",
    duration: "6:47",
    isPremium: true,
    contributorsCount: 1923,
    category: "Rare Recordings",
    unlockRequirement: "Community reached 50,000 total quest completions milestone"
  },
  {
    id: "3",
    title: "Metallica Family Photo Archive",
    description: "Personal photos from band members showcasing life on tour, family moments, and candid backstage memories.",
    type: "photo",
    thumbnail: "https://images.unsplash.com/photo-1699869310641-36adfb1928a9",
    alt: "Group of four musicians in black leather jackets standing together backstage",
    fullContent: "https://images.unsplash.com/photo-1645026870790-0e57a55252bf",
    unlockedDate: "2024-11-10",
    size: "24 photos",
    isPremium: false,
    contributorsCount: 3456,
    category: "Personal Archive",
    unlockRequirement: "Community photo submission quest completed with 3,456 fan photos"
  },
  {
    id: "4",
    title: "Songwriting Process - Enter Sandman",
    description: "Detailed breakdown of the songwriting and arrangement process for Enter Sandman, including original lyrics and chord progressions.",
    type: "document",
    thumbnail: "https://images.unsplash.com/photo-1730845984552-55d726daf184",
    alt: "Handwritten music sheet with guitar chord notations and lyrics on aged paper",
    fullContent: "https://images.unsplash.com/photo-1646454130993-27e6d12d8a46",
    unlockedDate: "2024-11-08",
    size: "12 pages",
    isPremium: false,
    contributorsCount: 1567,
    category: "Songwriting",
    unlockRequirement: "Lyric analysis quest completed by 1,567 community members"
  },
  {
    id: "5",
    title: "Live Performance - Moscow 1991 Full Concert",
    description: "Complete concert footage from the legendary Moscow performance in 1991, featuring the entire setlist in high quality.",
    type: "video",
    thumbnail: "https://images.unsplash.com/photo-1713388082578-9646ba029864",
    alt: "Massive outdoor concert crowd with stage lights illuminating thousands of fans",
    fullContent: "https://images.unsplash.com/photo-1547495827-28f487438ac4",
    unlockedDate: "2024-11-05",
    duration: "2:34:17",
    isPremium: true,
    contributorsCount: 4892,
    category: "Live Performances",
    unlockRequirement: "Historic concert quest unlocked through 4,892 community contributions"
  },
  {
    id: "6",
    title: "James Hetfield Guitar Collection Tour",
    description: "Exclusive walkthrough of James Hetfield's personal guitar collection, featuring stories behind each instrument.",
    type: "video",
    thumbnail: "https://images.unsplash.com/photo-1572405012950-4b6a1199d545",
    alt: "Collection of electric guitars mounted on wall display in professional lighting",
    fullContent: "https://images.unsplash.com/photo-1592474340135-09a860ab7410",
    unlockedDate: "2024-11-03",
    duration: "23:45",
    isPremium: false,
    contributorsCount: 2134,
    category: "Equipment",
    unlockRequirement: "Guitar gear quest completed with 2,134 fan submissions"
  }];


  const categories: FilterOption[] = [
  { id: 'all', label: 'All Categories', count: mockContent.length },
  { id: 'Studio Sessions', label: 'Studio Sessions', count: mockContent.filter((c) => c.category === 'Studio Sessions').length },
  { id: 'Rare Recordings', label: 'Rare Recordings', count: mockContent.filter((c) => c.category === 'Rare Recordings').length },
  { id: 'Personal Archive', label: 'Personal Archive', count: mockContent.filter((c) => c.category === 'Personal Archive').length },
  { id: 'Songwriting', label: 'Songwriting', count: mockContent.filter((c) => c.category === 'Songwriting').length },
  { id: 'Live Performances', label: 'Live Performances', count: mockContent.filter((c) => c.category === 'Live Performances').length },
  { id: 'Equipment', label: 'Equipment', count: mockContent.filter((c) => c.category === 'Equipment').length }];


  const types: FilterOption[] = [
  { id: 'all', label: 'All Types', count: mockContent.length },
  { id: 'video', label: 'Videos', count: mockContent.filter((c) => c.type === 'video').length },
  { id: 'audio', label: 'Audio', count: mockContent.filter((c) => c.type === 'audio').length },
  { id: 'photo', label: 'Photos', count: mockContent.filter((c) => c.type === 'photo').length },
  { id: 'document', label: 'Documents', count: mockContent.filter((c) => c.type === 'document').length }];


  const filteredContent = mockContent.filter((content) => {
    const matchesCategory = selectedCategory === 'all' || content.category === selectedCategory;
    const matchesType = selectedType === 'all' || content.type === selectedType;
    const matchesSearch = searchQuery === '' ||
    content.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    content.description.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesCategory && matchesType && matchesSearch;
  });

  const handleViewContent = (id: string) => {
    const content = mockContent.find((c) => c.id === id);
    setSelectedContent(content || null);
  };

  const handleCloseViewer = () => {
    setSelectedContent(null);
  };

  const handleClearFilters = () => {
    setSelectedCategory('all');
    setSelectedType('all');
    setSearchQuery('');
  };

  const handleExploreTier = (tierId: string) => {
    setActiveView('gallery');
    // Filter content by tier if needed
  };

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx:287:6" data-component-path="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx" data-component-line="287" data-component-file="UnlockedContentGalleryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <div data-component-id="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx:288:8" data-component-path="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx" data-component-line="288" data-component-file="UnlockedContentGalleryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20space-y-8%20p-8%22%7D" className="animate-pulse space-y-8 p-8">
          <div data-component-id="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx:289:10" data-component-path="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx" data-component-line="289" data-component-file="UnlockedContentGalleryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-8%20bg-muted%20rounded%20w-1%2F3%22%7D" className="h-8 bg-muted rounded w-1/3"></div>
          <div data-component-id="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx:290:10" data-component-path="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx" data-component-line="290" data-component-file="UnlockedContentGalleryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-4%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) =>
            <div data-component-id="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx:292:12" data-component-path="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx" data-component-line="292" data-component-file="UnlockedContentGalleryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-32%20bg-muted%20rounded-lg%22%7D" key={i} className="h-32 bg-muted rounded-lg"></div>
            )}
          </div>
          <div data-component-id="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx:295:10" data-component-path="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx" data-component-line="295" data-component-file="UnlockedContentGalleryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) =>
            <div data-component-id="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx:297:12" data-component-path="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx" data-component-line="297" data-component-file="UnlockedContentGalleryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-64%20bg-muted%20rounded-lg%22%7D" key={i} className="h-64 bg-muted rounded-lg"></div>
            )}
          </div>
        </div>
      </div>);

  }

  return (
    <div data-component-id="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx:306:4" data-component-path="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx" data-component-line="306" data-component-file="UnlockedContentGalleryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <div data-component-id="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx:307:6" data-component-path="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx" data-component-line="307" data-component-file="UnlockedContentGalleryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-6%20py-8%22%7D" className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div data-component-id="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx:309:8" data-component-path="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx" data-component-line="309" data-component-file="UnlockedContentGalleryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-8%22%7D" className="mb-8">
          <div data-component-id="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx:310:10" data-component-path="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx" data-component-line="310" data-component-file="UnlockedContentGalleryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20lg%3Aflex-row%20lg%3Aitems-center%20lg%3Ajustify-between%20mb-6%22%7D" className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
            <div data-component-id="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx:311:12" data-component-path="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx" data-component-line="311" data-component-file="UnlockedContentGalleryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
              <h1 data-component-id="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx:312:14" data-component-path="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx" data-component-line="312" data-component-file="UnlockedContentGalleryInteractive.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22text-4xl%20font-bold%20text-text-primary%20mb-2%22%2C%22textContent%22%3A%22Unlocked%20Content%20Gallery%22%7D" className="text-4xl font-bold text-text-primary mb-2">Unlocked Content Gallery</h1>
              <p data-component-id="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx:313:14" data-component-path="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx" data-component-line="313" data-component-file="UnlockedContentGalleryInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-text-secondary%20text-lg%22%2C%22textContent%22%3A%22Exclusive%20content%20unlocked%20through%20community%20achievements%22%7D" className="text-text-secondary text-lg">Exclusive content unlocked through community achievements</p>
            </div>
            <div data-component-id="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx:315:12" data-component-path="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx" data-component-line="315" data-component-file="UnlockedContentGalleryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-4%20mt-4%20lg%3Amt-0%22%7D" className="flex items-center space-x-4 mt-4 lg:mt-0">
              <button data-component-id="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx:316:14" data-component-path="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx" data-component-line="316" data-component-file="UnlockedContentGalleryInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                onClick={() => setActiveView('gallery')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors duration-300 ${
                activeView === 'gallery' ? 'bg-accent text-accent-foreground' : 'text-text-secondary hover:text-accent'}`
                }>

                <Icon data-component-id="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx:322:16" data-component-path="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx" data-component-line="322" data-component-file="UnlockedContentGalleryInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22PhotoIcon%22%7D" name="PhotoIcon" size={20} />
                <span data-component-id="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx:323:16" data-component-path="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx" data-component-line="323" data-component-file="UnlockedContentGalleryInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Gallery%22%7D">Gallery</span>
              </button>
              <button data-component-id="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx:325:14" data-component-path="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx" data-component-line="325" data-component-file="UnlockedContentGalleryInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                onClick={() => setActiveView('tiers')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors duration-300 ${
                activeView === 'tiers' ? 'bg-accent text-accent-foreground' : 'text-text-secondary hover:text-accent'}`
                }>

                <Icon data-component-id="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx:331:16" data-component-path="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx" data-component-line="331" data-component-file="UnlockedContentGalleryInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ShieldCheckIcon%22%7D" name="ShieldCheckIcon" size={20} />
                <span data-component-id="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx:332:16" data-component-path="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx" data-component-line="332" data-component-file="UnlockedContentGalleryInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Access%20Tiers%22%7D">Access Tiers</span>
              </button>
            </div>
          </div>

          {/* Stats Overview */}
          <StatsOverview data-component-id="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx:338:10" data-component-path="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx" data-component-line="338" data-component-file="UnlockedContentGalleryInteractive.tsx" data-component-name="StatsOverview" data-component-content="%7B%22elementName%22%3A%22StatsOverview%22%7D" stats={mockStats} />
        </div>

        {/* Content Views */}
        {activeView === 'gallery' ?
        <>
            {/* Filter Bar */}
            <FilterBar data-component-id="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx:345:12" data-component-path="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx" data-component-line="345" data-component-file="UnlockedContentGalleryInteractive.tsx" data-component-name="FilterBar" data-component-content="%7B%22elementName%22%3A%22FilterBar%22%7D"
            categories={categories}
            types={types}
            selectedCategory={selectedCategory}
            selectedType={selectedType}
            searchQuery={searchQuery}
            onCategoryChange={setSelectedCategory}
            onTypeChange={setSelectedType}
            onSearchChange={setSearchQuery}
            onClearFilters={handleClearFilters} />


            {/* Content Grid */}
            <div data-component-id="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx:358:12" data-component-path="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx" data-component-line="358" data-component-file="UnlockedContentGalleryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredContent.map((content) =>
            <ContentCard data-component-id="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx:360:12" data-component-path="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx" data-component-line="360" data-component-file="UnlockedContentGalleryInteractive.tsx" data-component-name="ContentCard" data-component-content="%7B%22elementName%22%3A%22ContentCard%22%7D"
              key={content.id}
              content={content}
              onView={handleViewContent} />

            )}
            </div>

            {/* Empty State */}
            {filteredContent.length === 0 &&
          <div data-component-id="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx:370:10" data-component-path="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx" data-component-line="370" data-component-file="UnlockedContentGalleryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20py-16%22%7D" className="text-center py-16">
                <Icon data-component-id="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx:371:16" data-component-path="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx" data-component-line="371" data-component-file="UnlockedContentGalleryInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22MagnifyingGlassIcon%22%2C%22className%22%3A%22mx-auto%20text-text-secondary%20mb-4%22%7D" name="MagnifyingGlassIcon" size={64} className="mx-auto text-text-secondary mb-4" />
                <h3 data-component-id="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx:372:16" data-component-path="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx" data-component-line="372" data-component-file="UnlockedContentGalleryInteractive.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-xl%20font-semibold%20text-text-primary%20mb-2%22%2C%22textContent%22%3A%22No%20content%20found%22%7D" className="text-xl font-semibold text-text-primary mb-2">No content found</h3>
                <p data-component-id="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx:373:16" data-component-path="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx" data-component-line="373" data-component-file="UnlockedContentGalleryInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-text-secondary%20mb-4%22%2C%22textContent%22%3A%22Try%20adjusting%20your%20filters%20or%20search%20terms%22%7D" className="text-text-secondary mb-4">Try adjusting your filters or search terms</p>
                <button data-component-id="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx:374:16" data-component-path="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx" data-component-line="374" data-component-file="UnlockedContentGalleryInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22bg-accent%20text-accent-foreground%20px-6%20py-2%20rounded-lg%20hover%3Abg-conversion-accent%20transition-colors%20duration-300%22%2C%22textContent%22%3A%22Clear%20Filters%22%7D"
              onClick={handleClearFilters}
              className="bg-accent text-accent-foreground px-6 py-2 rounded-lg hover:bg-conversion-accent transition-colors duration-300">

                  Clear Filters
                </button>
              </div>
          }
          </> :

        <>
            {/* Access Tiers */}
            <div data-component-id="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx:386:12" data-component-path="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx" data-component-line="386" data-component-file="UnlockedContentGalleryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-8%22%7D" className="mb-8">
              <h2 data-component-id="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx:387:14" data-component-path="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx" data-component-line="387" data-component-file="UnlockedContentGalleryInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-2xl%20font-bold%20text-text-primary%20mb-4%22%2C%22textContent%22%3A%22Content%20Access%20Tiers%22%7D" className="text-2xl font-bold text-text-primary mb-4">Content Access Tiers</h2>
              <p data-component-id="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx:388:14" data-component-path="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx" data-component-line="388" data-component-file="UnlockedContentGalleryInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-text-secondary%22%2C%22textContent%22%3A%22Unlock%20higher%20tiers%20by%20completing%20community%20quests%20and%20earning%20contribution%20points%22%7D" className="text-text-secondary">Unlock higher tiers by completing community quests and earning contribution points</p>
            </div>

            <div data-component-id="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx:391:12" data-component-path="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx" data-component-line="391" data-component-file="UnlockedContentGalleryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {mockTiers.map((tier) =>
            <TierCard data-component-id="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx:393:12" data-component-path="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx" data-component-line="393" data-component-file="UnlockedContentGalleryInteractive.tsx" data-component-name="TierCard" data-component-content="%7B%22elementName%22%3A%22TierCard%22%7D"
              key={tier.id}
              tier={tier}
              onExplore={handleExploreTier} />

            )}
            </div>
          </>
        }

        {/* Content Viewer Modal */}
        <ContentViewer data-component-id="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx:404:8" data-component-path="src/app/unlocked-content-gallery/components/UnlockedContentGalleryInteractive.tsx" data-component-line="404" data-component-file="UnlockedContentGalleryInteractive.tsx" data-component-name="ContentViewer" data-component-content="%7B%22elementName%22%3A%22ContentViewer%22%7D"
          content={selectedContent}
          onClose={handleCloseViewer} />

      </div>
    </div>);

};

export default UnlockedContentGalleryInteractive;